import React, { useEffect, useState } from "react";
import { useWA, PageHead, Card, Btn, Field, Toggle, ImageInput, Skeleton, ErrorBox, Badge, money } from "../ui";
import { siteLink } from "../api";

const NEW_PLAN = { k: "", name: "नया plan", days: 30, price: 99, limit: 30, color: "#22D3EE", tag: "", perks: ["30 days of access"], active: true, priority: false };

export default function Plans() {
  const { api, run, confirm, toast } = useWA();
  const [plans, setPlans] = useState(null);
  const [pay, setPay] = useState(null);
  const [payOn, setPayOn] = useState(true);
  const [features, setFeatures] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () =>
    api("settingsGet")
      .then((r) => {
        setPlans(r.settings.plans.map((p) => ({ ...p, perksText: p.perks.join("\n") })));
        setPay(r.settings.payment);
        setFeatures(r.settings.features);
        setPayOn(r.settings.features.payments);
        setDirty(false);
      })
      .catch((e) => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const upd = (i, k, v) => {
    setPlans((list) => list.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
    setDirty(true);
  };
  const move = (i, d) => {
    setPlans((list) => {
      const a = [...list];
      const j = i + d;
      if (j < 0 || j >= a.length) return a;
      [a[i], a[j]] = [a[j], a[i]];
      return a;
    });
    setDirty(true);
  };
  const add = () => {
    const k = "P" + Date.now().toString(36).toUpperCase().slice(-5);
    setPlans((list) => [...list, { ...NEW_PLAN, k, perksText: NEW_PLAN.perks.join("\n") }]);
    setDirty(true);
  };
  const remove = async (i) => {
    if (!(await confirm({ title: `"${plans[i].name}" plan हटाएँ?`, text: "जिन users के पास यह plan पहले से चालू है, उनका plan चलता रहेगा। नए लोग इसे नहीं खरीद पाएँगे। (सिर्फ छिपाना हो तो 'चालू' बंद कर दें)", danger: true }))) return;
    setPlans((list) => list.filter((_, j) => j !== i));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await run("settingsSave", { section: "plans", value: plans.map(({ perksText, ...p }) => ({ ...p, perks: perksText.split("\n") })) });
      await run("settingsSave", { section: "payment", value: pay });
      if (features && payOn !== features.payments) await run("settingsSave", { section: "features", value: { ...features, payments: payOn } });
      await load();
      toast("Plans और payment settings save हो गईं — वेबसाइट पर तुरंत दिखेंगी");
    } catch (e) {
      /* toast */
    }
    setBusy(false);
  };

  if (error) return <ErrorBox msg={error} retry={load} />;
  if (!plans || !pay) return <Skeleton rows={6} h={90} />;

  return (
    <div>
      <PageHead icon="fa-solid fa-crown" title="Plans & Prices" subtitle="Subscription plans, कीमत, दिन और UPI QR — यहीं से बदलें"
        actions={
          <>
            <a href={siteLink("/subscription")} target="_blank" rel="noreferrer"><Btn icon="fa-solid fa-arrow-up-right-from-square">Page देखें</Btn></a>
            <Btn variant="primary" icon="fa-solid fa-floppy-disk" loading={busy} onClick={save}>Save {dirty && "•"}</Btn>
          </>
        } />

      <Card className="mb-5" title="Payment लेना" icon="fa-solid fa-qrcode" subtitle="UPI QR और UPI ID जो subscription page पर दिखता है">
        <Toggle label="नए payments चालू" desc="बंद करने पर subscription page पर 'payments paused' दिखेगा। पहले से चालू plans चलते रहेंगे।" checked={payOn} onChange={(v) => { setPayOn(v); setDirty(true); }} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-2">
          <ImageInput label="UPI QR code image" square value={pay.upiQr} folder="payment" onChange={(v) => { setPay({ ...pay, upiQr: v }); setDirty(true); }} hint="नया QR लगाने पर तुरंत बदल जाएगा" />
          <div className="space-y-3">
            <Field label="UPI ID" hint="QR के नीचे दिखेगा (optional)"><input className="wa-input" value={pay.upiId} placeholder="yourname@upi" onChange={(e) => { setPay({ ...pay, upiId: e.target.value }); setDirty(true); }} /></Field>
            <Field label="Payee name"><input className="wa-input" value={pay.payeeName} onChange={(e) => { setPay({ ...pay, payeeName: e.target.value }); setDirty(true); }} /></Field>
            <Field label="QR के नीचे लाइन"><input className="wa-input" value={pay.note} onChange={(e) => { setPay({ ...pay, note: e.target.value }); setDirty(true); }} /></Field>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
        {plans.map((p, i) => (
          <div key={p.k} className="wa-card wa-in overflow-hidden" style={{ opacity: p.active ? 1 : 0.65 }}>
            <div className="h-1.5" style={{ background: p.color }} />
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <input type="color" value={p.color} onChange={(e) => upd(i, "color", e.target.value)} className="w-9 h-9 rounded-lg border wa-line cursor-pointer p-0.5" aria-label="Color" />
                <input className="wa-input !h-9 font-bold" value={p.name} onChange={(e) => upd(i, "name", e.target.value)} aria-label="Plan name" />
                <Badge tone="gray" className="shrink-0">{p.k}</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="कीमत ₹"><input type="number" min="0" className="wa-input" value={p.price} onChange={(e) => upd(i, "price", e.target.value)} /></Field>
                <Field label="दिन"><input type="number" min="1" className="wa-input" value={p.days} onChange={(e) => upd(i, "days", e.target.value)} /></Field>
                <Field label="Videos"><input type="number" min="1" className="wa-input" value={p.limit} onChange={(e) => upd(i, "limit", e.target.value)} /></Field>
              </div>
              <p className="text-[11.5px] wa-muted mt-1.5">{money(p.price)} · ₹{(Number(p.price) / Math.max(1, Number(p.days))).toFixed(1)} प्रति दिन</p>
              <Field label="Badge (ऊपर छोटा लेबल)" className="mt-3"><input className="wa-input" value={p.tag} placeholder="जैसे: Most popular" onChange={(e) => upd(i, "tag", e.target.value)} /></Field>
              <Field label="फायदे (हर लाइन में एक)" className="mt-3"><textarea rows={3} className="wa-input" value={p.perksText} onChange={(e) => upd(i, "perksText", e.target.value)} /></Field>
              <div className="border-t wa-line mt-3">
                <Toggle label="चालू (लोग खरीद सकें)" checked={p.active} onChange={(v) => upd(i, "active", v)} />
                <Toggle label="Priority support" checked={p.priority} onChange={(v) => upd(i, "priority", v)} />
              </div>
              <div className="flex items-center gap-1 pt-2 border-t wa-line">
                <Btn size="sm" icon="fa-solid fa-arrow-left" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move left" title="पहले दिखाएँ" />
                <Btn size="sm" icon="fa-solid fa-arrow-right" onClick={() => move(i, 1)} disabled={i === plans.length - 1} aria-label="Move right" title="बाद में दिखाएँ" />
                <Btn size="sm" className="ml-auto !text-rose-600" icon="fa-regular fa-trash-can" onClick={() => remove(i)}>हटाएँ</Btn>
              </div>
            </div>
          </div>
        ))}
        <button type="button" onClick={add} className="rounded-[18px] border-2 border-dashed wa-line min-h-[220px] grid place-items-center hover:opacity-80 wa-muted">
          <span className="text-center"><i className="fa-solid fa-plus text-2xl" /><span className="block font-semibold mt-2">नया plan जोड़ें</span></span>
        </button>
      </div>
      <p className="text-xs wa-muted mt-4">Plan code (जैसे M30) बदला नहीं जा सकता, ताकि पुराने payments सही plan से जुड़े रहें। बदलाव के बाद Save ज़रूर दबाएँ।</p>
    </div>
  );
}
